import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Add, Edit, Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Compass } from '../types';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const DashboardPage: React.FC = () => {
  const [recentCompasses, setRecentCompasses] = useState<Compass[]>([]);
  const [drafts, setDrafts] = useState<Compass[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [compassesResponse, draftsResponse] = await Promise.all([
        apiService.getCompasses(1, 5), // Get recent 5 compasses
        apiService.getDraftCompasses(),
      ]);
      
      setRecentCompasses(compassesResponse.data || []);
      setDrafts(draftsResponse);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Welcome back, {user?.name}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Continue your accountability journey
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 'fit-content' }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('/compass/new')}
                fullWidth
              >
                Create New Compass
              </Button>
              <Button
                variant="outlined"
                startIcon={<Visibility />}
                onClick={() => navigate('/compasses')}
                fullWidth
              >
                View All Compasses
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Draft Compasses */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 'fit-content' }}>
            <Typography variant="h6" gutterBottom>
              Draft Compasses ({drafts.length})
            </Typography>
            {drafts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No drafts found
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {drafts.slice(0, 3).map((draft) => (
                  <Card key={draft.id} variant="outlined" sx={{ cursor: 'pointer' }}>
                    <CardContent 
                      sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}
                      onClick={() => navigate(`/compass/edit/${draft.id}`)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2">
                          {draft.title || 'Untitled Draft'}
                        </Typography>
                        <Chip label="Draft" size="small" color="warning" />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(draft.updated_at)}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
                {drafts.length > 3 && (
                  <Button size="small" onClick={() => navigate('/compasses')}>
                    View all drafts
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Recent Compasses */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Recent Compasses
          </Typography>
          {recentCompasses.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                You haven't created any compasses yet
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('/compass/new')}
              >
                Create Your First Compass
              </Button>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {recentCompasses.map((compass) => (
                <Grid item xs={12} sm={6} md={4} key={compass.id}>
                  <Card sx={{ cursor: 'pointer' }}>
                    <CardContent onClick={() => navigate(`/compass/edit/${compass.id}`)}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" component="div" noWrap>
                          {compass.title || 'Untitled'}
                        </Typography>
                        <Chip 
                          label={compass.status} 
                          size="small" 
                          color={compass.status === 'submitted' ? 'success' : 'warning'} 
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {formatDate(compass.created_at)}
                      </Typography>
                      {compass.what_happened && (
                        <Typography variant="body2" sx={{ 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}>
                          {compass.what_happened}
                        </Typography>
                      )}
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button size="small" startIcon={<Edit />}>
                          {compass.status === 'draft' ? 'Continue' : 'View'}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
